import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Star } from "lucide-react";
import { Review } from "@/types/index";
import { formatDistanceToNow } from "date-fns";

interface LatestReviewCardProps {
  review: Review | null;
  onViewDetails?: (review: Review) => void;
}

export function LatestReviewCard({ review, onViewDetails }: LatestReviewCardProps) {
  if (!review) {
    return (
      <Card className="w-full">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
          <CardTitle className="text-sm font-medium">
            Latest Review
          </CardTitle>
        </CardHeader>
        <CardContent className="pb-4">
          <p className="text-sm text-muted-foreground">
            No reviews yet. Your latest review will appear here.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Format the date
  const timeAgo = formatDistanceToNow(new Date(review.created_at), { addSuffix: true });

  // Get sentiment color
  const getSentimentColor = () => {
    if (review.rating >= 4) return "text-green-600 dark:text-green-400";
    if (review.rating <= 2) return "text-red-600 dark:text-red-400";
    return "text-yellow-600 dark:text-yellow-400";
  };

  // Truncate text if too long
  const truncateText = (text: string, maxLength: number = 150) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const handleClick = () => {
    if (onViewDetails) {
      onViewDetails(review);
    }
  };

  return (
    <Card
      className="w-full cursor-pointer transition-colors hover:bg-accent/50"
      onClick={handleClick}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
        <CardTitle className="text-sm font-medium">
          Latest Review
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 pb-4">
        {/* Rating and time */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                size={14}
                className={
                  i < review.rating
                    ? "text-yellow-500 fill-yellow-500"
                    : "text-gray-300 dark:text-gray-600"
                }
              />
            ))}
            <span className={`ml-1.5 text-sm font-semibold ${getSentimentColor()}`}>
              {review.rating.toFixed(1)}
            </span>
          </div>
          <span className="text-xs text-muted-foreground">{timeAgo}</span>
        </div>

        {/* Customer name */}
        <div className="text-xs">
          <span className="font-medium">{review.customerName}</span>
        </div>

        {/* Review text */}
        {review.text && (
          <div className="text-xs text-muted-foreground border-l-2 border-gray-200 dark:border-gray-700 pl-2">
            {truncateText(review.text, 120)}
          </div>
        )}

        {/* Source badge */}
        {review.source && (
          <div className="flex items-center gap-2">
            <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
              {review.source}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
